/**
 * @lodex-pro/web-admin — Paelode's own billing (Phase 21 / Task 21.7a): the queries and
 * mutations behind Billing > Settings and Billing > Customers.
 *
 * Every route is under /v1/platform/billing and needs MANAGE_PLATFORM_BILLING (PLATFORM_ADMIN
 * only), which is who signs in to this console at all. The shapes mirror the API's DTOs
 * (routes/platform/billing/settings.ts, accounts.ts, rate-cards.ts).
 */
import { useApiMutation, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import type { FeatureTemplate } from '@lodex-pro/shared/features';
import { useQueryClient } from '@tanstack/react-query';
import { todayIn } from './billing-format';

export const BILLING_BASE = '/v1/platform/billing';

export type BillingStatus = 'ACTIVE' | 'NOT_BILLED' | 'PAUSED';
export type CustomerTenantType = 'HAULER' | 'OPERATOR' | 'DISPOSAL';

// ── Settings ─────────────────────────────────────────────────────────────────

export interface BillingSettings {
  readonly persisted: boolean;
  readonly legalName: string;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly postalCode: string | null;
  readonly logoDataUri: string | null;
  readonly remitInstructions: string | null;
  /** OPEN-212 PR A: Paelode's bank details; the account number itself is never sent back. */
  readonly remitBankName: string | null;
  readonly remitRoutingNumber: string | null;
  readonly remitAccountLast4: string | null;
  readonly remitEmail: string | null;
  readonly footerText: string | null;
  readonly defaultTermsDays: number;
  readonly invoicePrefix: string;
  readonly creditNotePrefix: string;
  readonly taxRateBps: number;
  readonly fromAddress: string;
  readonly fromDisplayName: string;
  readonly replyTo: string | null;
  readonly billingTimezone: string;
  readonly updatedAt: string | null;
  readonly updatedByUserId: string | null;
}

/** The sender as a send will see it (Task 21.5): null status = no guard wired (a laptop). */
export interface SenderView {
  readonly address: string;
  readonly displayName: string;
  readonly replyTo: string | null;
  readonly status: {
    readonly ok: boolean;
    readonly domain: string;
    /** NOT_ALLOWED_DOMAIN | NOT_VERIFIED | LOOKUP_FAILED (sender-guard.ts); kept open for new ones. */
    readonly reason?: string;
    readonly verifiedIdentity?: string;
  } | null;
}

export interface SettingsResponse {
  readonly settings: BillingSettings;
  readonly sender: SenderView;
}

/** What the PUT accepts (every field optional, merge semantics). */
export type SettingsUpdate = Partial<
  Omit<BillingSettings, 'persisted' | 'updatedAt' | 'updatedByUserId' | 'remitAccountLast4'> & {
    /** Write-only: omit to keep the stored number, null to clear it. */
    remitAccountNumber: string | null;
  }
>;

const SETTINGS_KEY = ['platform', 'billing', 'settings'] as const;

export function useBillingSettings(): ReturnType<typeof useApiQuery<SettingsResponse>> {
  const opts = useApiOpts();
  return useApiQuery<SettingsResponse>(opts, `${BILLING_BASE}/settings`, {
    queryKey: SETTINGS_KEY,
  });
}

export function useSaveBillingSettings(handlers: {
  onSuccess: () => void;
  onError: (err: unknown) => void;
}): ReturnType<typeof useApiMutation<SettingsUpdate, { settings: BillingSettings }>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<SettingsUpdate, { settings: BillingSettings }>(
    opts,
    `${BILLING_BASE}/settings`,
    'PUT',
    {
      onSuccess: () => {
        // The PUT returns the settings only; the sender check is the GET's.
        void qc.invalidateQueries({ queryKey: SETTINGS_KEY });
        handlers.onSuccess();
      },
      onError: handlers.onError,
    },
  );
}

// ── Customers (accounts + subscriptions) ─────────────────────────────────────

export interface SubscriptionSummary {
  readonly id: string;
  readonly rateCardId: string | null;
  readonly rateCardName: string | null;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly addOnCodes: readonly string[];
}

export interface CustomerRow {
  readonly tenantId: string;
  readonly tenantName: string;
  readonly tenantType: CustomerTenantType;
  readonly hasAccount: boolean;
  readonly billingStatus: BillingStatus;
  readonly billToName: string | null;
  readonly toEmails: readonly string[];
  readonly latestSubscription: SubscriptionSummary | null;
}

export interface BillingAccount {
  readonly id: string;
  readonly customerTenantId: string;
  readonly billingStatus: BillingStatus;
  readonly billToName: string | null;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly postalCode: string | null;
  readonly toEmails: readonly string[];
  readonly ccEmails: readonly string[];
  readonly termsDaysOverride: number | null;
  readonly poReference: string | null;
  readonly notes: string | null;
  /** OPEN-212 PR A (DEC-387 D7): print Paelode's bank details on this customer's invoices. */
  readonly showRemitBankDetails: boolean;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly updatedByUserId: string | null;
}

export interface AccountResponse {
  readonly customer: {
    readonly tenantId: string;
    readonly name: string;
    readonly tenantType: CustomerTenantType;
  };
  readonly tenantDefaults: {
    readonly billToName: string;
    readonly businessAddress: string | null;
    readonly primaryEmail: string | null;
  };
  readonly account: BillingAccount | null;
  readonly billingStatus: BillingStatus;
}

export type AccountUpdate = Partial<
  Omit<BillingAccount, 'id' | 'customerTenantId' | 'createdAt' | 'updatedAt' | 'updatedByUserId'>
>;

export interface Subscription {
  readonly id: string;
  readonly customerTenantId: string;
  readonly rateCardId: string;
  readonly rateCardName: string | null;
  /** 21.7d: the hauler plan the card sells (0232); null when the card names none. */
  readonly rateCardFeatureTemplate?: FeatureTemplate | null;
  readonly addOnCodes: readonly string[];
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly notes: string | null;
  readonly createdByUserId: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /** #1014: until when "Undo end" is accepted (history read only); null = not undoable. */
  readonly undoEndUntil?: string | null;
}

export interface CreateSubscriptionBody {
  readonly rateCardId: string;
  readonly startsOn: string;
  readonly endsOn?: string | null;
  readonly addOnCodes?: readonly string[];
  readonly notes?: string | null;
}

export interface RateCardSummary {
  readonly id: string;
  readonly name: string;
  readonly tenantType: CustomerTenantType;
  readonly description: string | null;
  readonly isArchived: boolean;
  /** 0232: the hauler feature plan this card sells (hauler cards only). */
  readonly featureTemplate: FeatureTemplate | null;
  readonly versionCount: number;
  readonly latestPublishedVersion: {
    readonly id: string;
    readonly versionNumber: number;
    readonly effectiveFrom: string;
  } | null;
}

const ACCOUNTS_KEY = ['platform', 'billing', 'accounts'] as const;
const accountKey = (tenantId: string): readonly unknown[] => [...ACCOUNTS_KEY, tenantId];
const subscriptionsKey = (tenantId: string): readonly unknown[] => [
  ...ACCOUNTS_KEY,
  tenantId,
  'subscriptions',
];

export function useBillingCustomers(): ReturnType<
  typeof useApiQuery<{ customers: readonly CustomerRow[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ customers: readonly CustomerRow[] }>(opts, `${BILLING_BASE}/accounts`, {
    queryKey: ACCOUNTS_KEY,
  });
}

export function useBillingAccount(
  tenantId: string,
): ReturnType<typeof useApiQuery<AccountResponse>> {
  const opts = useApiOpts();
  return useApiQuery<AccountResponse>(opts, `${BILLING_BASE}/accounts/${tenantId}`, {
    queryKey: accountKey(tenantId),
  });
}

export function useSubscriptions(
  tenantId: string,
): ReturnType<typeof useApiQuery<{ subscriptions: readonly Subscription[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ subscriptions: readonly Subscription[] }>(
    opts,
    `${BILLING_BASE}/accounts/${tenantId}/subscriptions`,
    { queryKey: subscriptionsKey(tenantId) },
  );
}

export function useRateCards(): ReturnType<
  typeof useApiQuery<{ rateCards: readonly RateCardSummary[] }>
> {
  const opts = useApiOpts();
  return useApiQuery<{ rateCards: readonly RateCardSummary[] }>(
    opts,
    `${BILLING_BASE}/rate-cards`,
    {
      queryKey: ['platform', 'billing', 'rate-cards'],
    },
  );
}

function useInvalidateCustomer(tenantId: string): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    void qc.invalidateQueries({ queryKey: accountKey(tenantId) });
    void qc.invalidateQueries({ queryKey: subscriptionsKey(tenantId) });
  };
}

interface Handlers {
  readonly onSuccess: () => void;
  readonly onError: (err: unknown) => void;
}

export function useSaveAccount(
  tenantId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<AccountUpdate, { account: BillingAccount }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateCustomer(tenantId);
  return useApiMutation<AccountUpdate, { account: BillingAccount }>(
    opts,
    `${BILLING_BASE}/accounts/${tenantId}`,
    'PUT',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

export function useCreateSubscription(
  tenantId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<CreateSubscriptionBody, { subscription: Subscription }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateCustomer(tenantId);
  return useApiMutation<CreateSubscriptionBody, { subscription: Subscription }>(
    opts,
    `${BILLING_BASE}/accounts/${tenantId}/subscriptions`,
    'POST',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

/** Ends ONE subscription; the hook is made per row (its id is in the path). */
export function useEndSubscription(
  tenantId: string,
  subscriptionId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<{ endsOn: string }, { subscription: Subscription }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateCustomer(tenantId);
  return useApiMutation<{ endsOn: string }, { subscription: Subscription }>(
    opts,
    `${BILLING_BASE}/subscriptions/${subscriptionId}/end`,
    'POST',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

/** Reopens ONE subscription ended in the last few minutes (POST /subscriptions/:id/undo-end). */
export function useUndoEndSubscription(
  tenantId: string,
  subscriptionId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<Record<string, never>, { subscription: Subscription }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateCustomer(tenantId);
  return useApiMutation<Record<string, never>, { subscription: Subscription }>(
    opts,
    `${BILLING_BASE}/subscriptions/${subscriptionId}/undo-end`,
    'POST',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

/**
 * Today (YYYY-MM-DD) in Paelode's billing time zone, or null until the settings have loaded:
 * a date default or a "has it ended" check is never taken from a fallback zone (#940 LOW-1).
 */
export function useBillingToday(): string | null {
  const s = useBillingSettings();
  return s.data === undefined ? null : todayIn(s.data.settings.billingTimezone);
}

// ── Rate cards (Task 21.7b) ──────────────────────────────────────────────────

export type ChargeKind = 'FLAT_MONTHLY' | 'PER_UNIT' | 'TIERED_BAND' | 'PER_MODULE';
export type VersionStatus = 'DRAFT' | 'PUBLISHED';

export interface RateCardBand {
  readonly fromQty: number;
  readonly toQty: number | null;
  readonly priceCents: number;
}

export interface RateCardLine {
  readonly id: string;
  readonly code: string;
  readonly description: string;
  readonly chargeKind: ChargeKind;
  readonly meter: string | null;
  readonly moduleCode: string | null;
  readonly unitPriceCents: number;
  readonly includedQuantity: string;
  readonly bands: readonly RateCardBand[] | null;
  readonly isAddOn: boolean;
  readonly sortOrder: number;
}

export interface RateCardVersion {
  readonly id: string;
  readonly rateCardId: string;
  readonly versionNumber: number;
  readonly effectiveFrom: string;
  readonly status: VersionStatus;
  readonly publishedAt: string | null;
  readonly publishedByUserId: string | null;
  readonly createdByUserId: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly lines: readonly RateCardLine[];
}

export interface RateCardDetail {
  readonly id: string;
  readonly name: string;
  readonly tenantType: CustomerTenantType;
  readonly description: string | null;
  readonly isArchived: boolean;
  readonly featureTemplate: FeatureTemplate | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly versions: readonly RateCardVersion[];
}

/** One line as the version PUT takes it: money as dollar strings, never floats. */
export interface RateCardLineBody {
  readonly code: string;
  readonly description: string;
  readonly chargeKind: ChargeKind;
  readonly meter: string | null;
  readonly moduleCode: string | null;
  readonly unitPrice: string;
  readonly includedQuantity: string;
  readonly bands: ReadonlyArray<{ fromQty: number; toQty: number | null; price: string }> | null;
  readonly isAddOn: boolean;
  readonly sortOrder: number;
}

export interface VersionBody {
  readonly effectiveFrom: string;
  readonly notes: string | null;
  readonly lines: readonly RateCardLineBody[];
}

const RATE_CARDS_KEY = ['platform', 'billing', 'rate-cards'] as const;
const rateCardKey = (id: string): readonly unknown[] => [...RATE_CARDS_KEY, id];

export function useRateCardList(
  includeArchived: boolean,
): ReturnType<typeof useApiQuery<{ rateCards: readonly RateCardSummary[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ rateCards: readonly RateCardSummary[] }>(
    opts,
    `${BILLING_BASE}/rate-cards${includeArchived ? '?includeArchived=true' : ''}`,
    { queryKey: [...RATE_CARDS_KEY, { includeArchived }] },
  );
}

export function useRateCard(
  id: string,
): ReturnType<typeof useApiQuery<{ rateCard: RateCardDetail }>> {
  const opts = useApiOpts();
  return useApiQuery<{ rateCard: RateCardDetail }>(opts, `${BILLING_BASE}/rate-cards/${id}`, {
    queryKey: rateCardKey(id),
  });
}

function useInvalidateRateCards(): () => void {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: RATE_CARDS_KEY });
  };
}

export function useCreateRateCard(h: Handlers): ReturnType<
  typeof useApiMutation<
    {
      name: string;
      tenantType: CustomerTenantType;
      description: string | null;
      featureTemplate?: FeatureTemplate | null;
    },
    { rateCard: RateCardDetail }
  >
> {
  const opts = useApiOpts();
  const invalidate = useInvalidateRateCards();
  return useApiMutation<
    {
      name: string;
      tenantType: CustomerTenantType;
      description: string | null;
      featureTemplate?: FeatureTemplate | null;
    },
    { rateCard: RateCardDetail }
  >(opts, `${BILLING_BASE}/rate-cards`, 'POST', {
    onSuccess: () => {
      invalidate();
      h.onSuccess();
    },
    onError: h.onError,
  });
}

export function usePatchRateCard(
  id: string,
  h: Handlers,
): ReturnType<
  typeof useApiMutation<
    Partial<{
      name: string;
      description: string | null;
      isArchived: boolean;
      featureTemplate: FeatureTemplate | null;
    }>,
    { rateCard: RateCardDetail }
  >
> {
  const opts = useApiOpts();
  const invalidate = useInvalidateRateCards();
  return useApiMutation<
    Partial<{
      name: string;
      description: string | null;
      isArchived: boolean;
      featureTemplate: FeatureTemplate | null;
    }>,
    { rateCard: RateCardDetail }
  >(opts, `${BILLING_BASE}/rate-cards/${id}`, 'PATCH', {
    onSuccess: () => {
      invalidate();
      h.onSuccess();
    },
    onError: h.onError,
  });
}

export function useCreateVersion(
  rateCardId: string,
  h: Handlers,
): ReturnType<
  typeof useApiMutation<
    { effectiveFrom: string; notes?: string | null; copyFromVersionId?: string },
    { version: RateCardVersion }
  >
> {
  const opts = useApiOpts();
  const invalidate = useInvalidateRateCards();
  return useApiMutation<
    { effectiveFrom: string; notes?: string | null; copyFromVersionId?: string },
    { version: RateCardVersion }
  >(opts, `${BILLING_BASE}/rate-cards/${rateCardId}/versions`, 'POST', {
    onSuccess: () => {
      invalidate();
      h.onSuccess();
    },
    onError: h.onError,
  });
}

export function useSaveVersion(
  versionId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<VersionBody, { version: RateCardVersion }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateRateCards();
  return useApiMutation<VersionBody, { version: RateCardVersion }>(
    opts,
    `${BILLING_BASE}/rate-card-versions/${versionId}`,
    'PUT',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

export function usePublishVersion(
  versionId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<Record<string, never>, { version: RateCardVersion }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateRateCards();
  return useApiMutation<Record<string, never>, { version: RateCardVersion }>(
    opts,
    `${BILLING_BASE}/rate-card-versions/${versionId}/publish`,
    'POST',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

export function useDeleteVersion(
  versionId: string,
  h: Handlers,
): ReturnType<typeof useApiMutation<Record<string, never>, { deleted: true; versionId: string }>> {
  const opts = useApiOpts();
  const invalidate = useInvalidateRateCards();
  return useApiMutation<Record<string, never>, { deleted: true; versionId: string }>(
    opts,
    `${BILLING_BASE}/rate-card-versions/${versionId}`,
    'DELETE',
    {
      onSuccess: () => {
        invalidate();
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

// ── Usage and invoices (Task 21.7c) ──────────────────────────────────────────

export type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOID';

export interface UsageMeter {
  readonly meter: string;
  readonly kind: string;
  /** Decimal string, or null when unknown (nothing captured, or not collected). */
  readonly value: string | null;
  readonly daysCaptured: number | null;
  readonly daysInPeriod: number;
  readonly collected: boolean;
  readonly moduleCodes?: readonly string[];
}

export interface UsageResponse {
  readonly month: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly timezone: string;
  readonly daysInPeriod: number;
  readonly customers: ReadonlyArray<{
    readonly tenantId: string;
    readonly tenantName: string;
    readonly tenantType: CustomerTenantType;
    readonly billingStatus: BillingStatus;
    readonly meters: readonly UsageMeter[];
  }>;
}

export interface InvoiceWarning {
  readonly code: string;
  readonly message: string;
}

export interface InvoiceListRow {
  readonly id: string;
  readonly customerTenantId: string;
  readonly customerName: string | null;
  readonly customerType: string | null;
  readonly status: InvoiceStatus;
  readonly invoiceNumber: string | null;
  readonly month: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly subtotalCents: number;
  readonly taxCents: number;
  readonly totalCents: number;
  readonly amountPaidCents: number;
  readonly generationSource: string;
  readonly warningCount: number;
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly emailStatus: string | null;
}

export interface InvoiceLine {
  readonly id: string;
  readonly sortOrder: number;
  readonly kind: 'CHARGE' | 'ADJUSTMENT';
  readonly chargeKind: string | null;
  readonly lineCode: string | null;
  readonly description: string;
  readonly meter: string | null;
  readonly meterValue: string | null;
  readonly moduleCode: string | null;
  readonly includedQuantity: string;
  readonly quantity: string;
  readonly unitPriceCents: number;
  readonly amountCents: number;
  readonly reason: string | null;
}

export interface BillTo {
  readonly name: string;
  readonly addressLine1: string | null;
  readonly addressLine2: string | null;
  readonly city: string | null;
  readonly state: string | null;
  readonly postalCode: string | null;
  readonly tenantBusinessAddress: string | null;
  readonly toEmails: readonly string[];
  readonly ccEmails: readonly string[];
  readonly poReference: string | null;
}

export interface Invoice {
  readonly id: string;
  readonly customerTenantId: string;
  readonly customerName: string | null;
  readonly customerType: string | null;
  readonly status: InvoiceStatus;
  readonly invoiceNumber: string | null;
  readonly month: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly issueDate: string | null;
  readonly dueDate: string | null;
  readonly termsDays: number | null;
  readonly subtotalCents: number;
  readonly taxRateBps: number;
  readonly taxCents: number;
  readonly totalCents: number;
  readonly amountPaidCents: number;
  /** Phase 21 / Task 21.6a (0232): total − paid, and the day it was paid in full. */
  readonly balanceCents: number;
  readonly paidOn: string | null;
  /** 21.6b: when the last payment reminder went out. */
  readonly lastRemindedAt: string | null;
  readonly billTo: BillTo | null;
  readonly warnings: readonly InvoiceWarning[] | null;
  readonly generationSource: string;
  readonly notes: string | null;
  readonly sentAt: string | null;
  readonly emailStatus: string | null;
  readonly emailError: string | null;
  readonly emailedAt: string | null;
  readonly pdf: { readonly sha256: string | null; readonly sizeBytes: number | null } | null;
  /** The billing account's CURRENT addresses: what a send, resend or reminder will use. */
  readonly recipients: { readonly to: readonly string[]; readonly cc: readonly string[] };
  /** The newest email that went out, with its recorded recipients (null on older rows). */
  readonly lastEmail: {
    readonly at: string;
    readonly kind: 'INVOICE' | 'REMINDER' | 'VOID_NOTICE';
    readonly to: readonly string[] | null;
    readonly cc: readonly string[] | null;
  } | null;
  readonly voidAt: string | null;
  readonly voidReason: string | null;
  readonly lines: readonly InvoiceLine[];
}

export interface InvoicePreview {
  readonly month: string;
  readonly customerTenantId: string;
  readonly customerName: string;
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly billingStatus: string;
  readonly subscription: { readonly rateCardName: string; readonly startsOn: string } | null;
  readonly rateCardVersion: {
    readonly versionNumber: number;
    readonly effectiveFrom: string;
  } | null;
  readonly lines: ReadonlyArray<{
    readonly description: string;
    readonly quantity: string;
    readonly unitPriceCents: number;
    readonly amountCents: number;
  }>;
  readonly subtotalCents: number;
  readonly taxCents: number;
  readonly totalCents: number;
  readonly warnings: readonly InvoiceWarning[];
  readonly skipReason: string | null;
  readonly skipDetail: Record<string, unknown> | null;
}

export interface InvoiceRun {
  readonly periodStart: string;
  readonly periodEnd: string;
  readonly startedAt: string;
  readonly finishedAt: string | null;
  readonly customersConsidered: number;
  readonly draftsCreated: number;
  readonly skipped: ReadonlyArray<{
    readonly customerTenantId: string;
    readonly customerName: string;
    readonly reason: string;
  }>;
  readonly source: 'MANUAL' | 'SCHEDULED';
  readonly triggeredByUserId: string | null;
}

export interface Delivery {
  readonly status: string;
  readonly messageId?: string;
  readonly stage?: string;
  readonly error?: string;
  readonly pdfRendered: boolean;
}

const INVOICES_KEY = ['platform', 'billing', 'invoices'] as const;

export function useUsage(month: string): ReturnType<typeof useApiQuery<UsageResponse>> {
  const opts = useApiOpts();
  return useApiQuery<UsageResponse>(opts, `${BILLING_BASE}/usage?month=${month}`, {
    queryKey: ['platform', 'billing', 'usage', month],
  });
}

export function useSnapshotNow(
  h: Handlers,
): ReturnType<
  typeof useApiMutation<Record<string, never>, { usageDate: string; claimed: boolean }>
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<Record<string, never>, { usageDate: string; claimed: boolean }>(
    opts,
    `${BILLING_BASE}/usage/snapshot`,
    'POST',
    {
      onSuccess: () => {
        void qc.invalidateQueries({ queryKey: ['platform', 'billing', 'usage'] });
        h.onSuccess();
      },
      onError: h.onError,
    },
  );
}

/** The list query string from the filters (only the ones set). */
export function invoiceListQuery(f: {
  readonly status: string;
  readonly month: string;
  readonly customer: string;
}): string {
  const p = new URLSearchParams();
  if (f.status !== '') p.set('status', f.status);
  if (f.month !== '') p.set('month', f.month);
  if (f.customer !== '') p.set('customer', f.customer);
  const q = p.toString();
  return q === '' ? '' : `?${q}`;
}

export function useInvoices(
  query: string,
): ReturnType<typeof useApiQuery<{ invoices: readonly InvoiceListRow[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ invoices: readonly InvoiceListRow[] }>(
    opts,
    `${BILLING_BASE}/invoices${query}`,
    { queryKey: [...INVOICES_KEY, 'list', query] },
  );
}

export function useInvoice(id: string): ReturnType<typeof useApiQuery<Invoice>> {
  const opts = useApiOpts();
  return useApiQuery<Invoice>(opts, `${BILLING_BASE}/invoices/${id}`, {
    queryKey: [...INVOICES_KEY, id],
  });
}

export function useInvoiceRuns(): ReturnType<typeof useApiQuery<{ runs: readonly InvoiceRun[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ runs: readonly InvoiceRun[] }>(opts, `${BILLING_BASE}/invoice-runs`, {
    queryKey: [...INVOICES_KEY, 'runs'],
  });
}

/** A billing invoice mutation at `path`; every success refreshes the invoice queries. */
export function useInvoiceMutation<TBody, TRes>(
  path: string,
  method: 'POST' | 'PATCH' | 'DELETE',
  h: Handlers,
): ReturnType<typeof useApiMutation<TBody, TRes>> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useApiMutation<TBody, TRes>(opts, `${BILLING_BASE}${path}`, method, {
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: INVOICES_KEY });
      h.onSuccess();
    },
    onError: h.onError,
  });
}

// ── Phase 21 / Task 21.7d: payments and aging ────────────────────────────────────────

export const PAYMENT_METHODS = ['CHECK', 'ACH', 'WIRE', 'CARD_OFFLINE', 'OTHER'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** One ledger row (21.6a): a PAYMENT, or the REVERSAL of one. Rows are never edited. */
export interface Payment {
  readonly id: string;
  readonly invoiceId: string;
  readonly kind: 'PAYMENT' | 'REVERSAL';
  readonly amountCents: number;
  readonly reversesPaymentId: string | null;
  readonly reversedByPaymentId: string | null;
  readonly paymentDate: string;
  readonly method: PaymentMethod | null;
  readonly reference: string | null;
  readonly notes: string | null;
  readonly reason: string | null;
  readonly recordedBy: string | null;
  readonly recordedAt: string;
}

export interface PaymentListRow extends Payment {
  readonly invoiceNumber: string | null;
  readonly customerTenantId: string;
  readonly customerName: string | null;
}

export interface PaymentsPage {
  readonly payments: readonly PaymentListRow[];
  readonly nextCursor: string | null;
  /** Summed by the server over the whole filter, never from this page. */
  readonly totals: {
    readonly count: number;
    readonly paymentsCents: number;
    readonly reversalsCents: number;
    readonly netCents: number;
  };
}

export interface RecordPaymentBody {
  readonly amount: string;
  readonly paymentDate: string;
  readonly method: PaymentMethod;
  readonly reference?: string | null;
  readonly notes?: string | null;
}

export type AgingBucket = 'CURRENT' | 'D1_30' | 'D31_60' | 'D61_90' | 'D90_PLUS';
export type DueClass = 'OVERDUE' | 'DUE_THIS_WEEK' | 'DUE_LATER';

export interface AgingInvoice {
  readonly invoiceId: string;
  readonly invoiceNumber: string;
  readonly customerTenantId: string;
  readonly customerName: string | null;
  readonly issueDate: string;
  readonly dueDate: string;
  readonly totalCents: number;
  readonly amountPaidCents: number;
  readonly balanceCents: number;
  /** OPEN-212 PR B: an ACH debit settling against the balance (still owed until it settles). */
  readonly pendingCents: number;
  readonly daysPastDue: number;
  readonly bucket: AgingBucket;
  readonly dueClass: DueClass;
  readonly lastRemindedAt: string | null;
}

export interface AgingTotal {
  readonly count: number;
  readonly balanceCents: number;
}

export interface AgingResponse {
  readonly asOf: string;
  readonly timezone: string;
  readonly invoices: readonly AgingInvoice[];
  readonly totals: {
    readonly all: AgingTotal;
    readonly byBucket: Readonly<Record<AgingBucket, AgingTotal>>;
    readonly byDueClass: Readonly<Record<DueClass, AgingTotal>>;
    readonly pendingCents: number;
  };
}

export interface RemindOverdueResponse {
  readonly asOf: string;
  readonly results: ReadonlyArray<{
    readonly invoiceId: string;
    readonly invoiceNumber: string | null;
    readonly status: 'SENT' | 'FAILED' | 'SKIPPED';
    readonly reason?: string;
    readonly error?: string | null;
  }>;
  readonly counts: { readonly sent: number; readonly failed: number; readonly skipped: number };
  /** Still due a reminder today after this batch (the next click sends the next batch). */
  readonly remaining: number;
}

// The payments and aging queries live under INVOICES_KEY, so every invoice mutation (a payment,
// a reversal, a reminder, a send or a void) refreshes them too.
export function useInvoicePayments(
  invoiceId: string,
): ReturnType<typeof useApiQuery<{ payments: readonly Payment[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ payments: readonly Payment[] }>(
    opts,
    `${BILLING_BASE}/invoices/${invoiceId}/payments`,
    { queryKey: [...INVOICES_KEY, invoiceId, 'payments'] },
  );
}

// ── OPEN-212 PR B: paying an invoice online by ACH (Stripe) ──────────────────────────

export type PaymentAttemptStatus = 'OPEN' | 'PENDING' | 'SUCCEEDED' | 'FAILED' | 'CANCELED';

export interface PaymentAttempt {
  readonly id: string;
  readonly status: PaymentAttemptStatus;
  readonly source: 'ADMIN' | 'PAY_LINK' | 'PORTAL' | 'AUTOPAY';
  readonly autopay: boolean;
  readonly amountCents: number;
  /** Only while OPEN: the Stripe Checkout link to send the customer. */
  readonly checkoutUrl: string | null;
  readonly checkoutExpiresAt: string | null;
  readonly paymentIntentId: string | null;
  readonly paymentId: string | null;
  readonly failureCode: string | null;
  readonly failureMessage: string | null;
  readonly createdAt: string;
  readonly submittedAt: string | null;
  readonly settledAt: string | null;
  readonly failedAt: string | null;
}

export interface PaySession {
  readonly attemptId: string;
  readonly url: string;
  readonly expiresAt: string;
  readonly amountCents: number;
  readonly reused: boolean;
}

export function usePaymentAttempts(
  invoiceId: string,
): ReturnType<typeof useApiQuery<{ attempts: readonly PaymentAttempt[] }>> {
  const opts = useApiOpts();
  return useApiQuery<{ attempts: readonly PaymentAttempt[] }>(
    opts,
    `${BILLING_BASE}/invoices/${invoiceId}/payment-attempts`,
    { queryKey: [...INVOICES_KEY, invoiceId, 'attempts'] },
  );
}

/** "?from=…&to=…&customer=…&cursor=…" from the Payments filters (empty parts left out). */
export function paymentsQuery(f: {
  from: string;
  to: string;
  customer: string;
  cursor?: string | null;
}): string {
  const p = new URLSearchParams();
  if (f.from !== '') p.set('from', f.from);
  if (f.to !== '') p.set('to', f.to);
  if (f.customer !== '') p.set('customer', f.customer);
  if (f.cursor !== undefined && f.cursor !== null) p.set('cursor', f.cursor);
  const q = p.toString();
  return q === '' ? '' : `?${q}`;
}

export function usePayments(query: string): ReturnType<typeof useApiQuery<PaymentsPage>> {
  const opts = useApiOpts();
  return useApiQuery<PaymentsPage>(opts, `${BILLING_BASE}/payments${query}`, {
    queryKey: [...INVOICES_KEY, 'payments', query],
  });
}

export function useAging(customer: string): ReturnType<typeof useApiQuery<AgingResponse>> {
  const opts = useApiOpts();
  const q = customer === '' ? '' : `?customer=${customer}`;
  return useApiQuery<AgingResponse>(opts, `${BILLING_BASE}/aging${q}`, {
    queryKey: [...INVOICES_KEY, 'aging', customer],
  });
}
